import React, { useState, useEffect, useRef, useCallback } from "react";
import { useIsMobile } from "../hooks/useIsMobile";

/**
 * PageHeader - GCHL Brand styled page header with logo and title
 *
 * Uses Montserrat font with uppercase tracking for titles per brand guide.
 * Includes glassmorphism container and accessible clickable logo modal.
 *
 * @param {Object} props
 * @param {string} props.title - Page title (displayed uppercase)
 * @param {string} props.updatedText - Optional subtitle/meta text
 * @param {React.ReactNode} props.actions - Optional action buttons
 */
const PageHeader = ({ title, updatedText, actions }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Handle Escape key and focus trap
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsModalOpen(false);
      return;
    }

    // Focus trap - Tab/Shift+Tab cycles within modal
    if (e.key === 'Tab' && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, []);

  // Focus management for modal
  useEffect(() => {
    if (isModalOpen) {
      // Store current focus to restore later
      previousActiveElement.current = document.activeElement;
      // Focus close button when modal opens
      closeButtonRef.current?.focus();
      // Add keydown listener
      document.addEventListener('keydown', handleKeyDown);
    } else {
      // Restore focus when modal closes
      previousActiveElement.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, handleKeyDown]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="relative mb-6">
      {/* Glassmorphism Container */}
      <div className="bg-white/60 dark:bg-navy-900/70 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex items-center space-x-4 sm:space-x-6">
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                type="button"
                onClick={openModal}
                className="focus:outline-none focus:ring-2 focus:ring-forest-500 focus:ring-offset-2 rounded-lg"
                aria-label="View full size logo"
              >
                <img
                  src="/assets/logo.png"
                  alt="Gulf Coast Hackers League Logo"
                  className={`
                    hover:opacity-80 transition-opacity object-contain
                    ${isMobile ? "w-16 h-16" : "w-24 h-24 sm:w-32 sm:h-32"}
                  `}
                />
              </button>
            </div>

            {/* Text Content */}
            <div className="flex-1 min-w-0">
              {/* Title - Montserrat uppercase with 0.05em tracking (Brand Style) */}
              {/* Fallback color before gradient for older browsers */}
              <h1 className="
                font-display font-extrabold uppercase tracking-wider
                text-2xl sm:text-3xl md:text-4xl
                truncate leading-tight
                text-forest-950 dark:text-gold-300
                bg-gradient-to-r from-forest-950 via-forest-700 to-gold-500
                bg-clip-text text-transparent
                dark:from-gold-300 dark:via-forest-400 dark:to-gold-400
              ">
                {title}
              </h1>

              {/* Subtitle - Inter meta style */}
              {updatedText && (
                <p className="
                  mt-1 font-body text-meta
                  text-gray-600 dark:text-navy-400
                ">
                  {updatedText}
                </p>
              )}
            </div>

            {/* Optional Actions */}
            {actions && (
              <div className="flex-shrink-0 flex gap-2">
                {actions}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logo Modal - Accessible with focus trap and Esc key */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          role="presentation"
        >
          <div
            ref={modalRef}
            className="bg-white dark:bg-navy-900 p-4 rounded-2xl shadow-2xl max-w-lg w-full relative animate-scale-in"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="League logo enlarged view"
          >
            {/* Close button - inside card for visibility */}
            <button
              ref={closeButtonRef}
              onClick={closeModal}
              className="
                absolute top-2 right-2
                bg-navy-900/80 dark:bg-white/20
                text-white hover:bg-navy-800 dark:hover:bg-white/30
                p-2 rounded-full
                min-w-touch min-h-touch
                flex items-center justify-center
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-gold-400 focus:ring-offset-2
              "
              aria-label="Close logo modal"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Logo image */}
            <img
              src="/assets/logo.png"
              alt="Gulf Coast Hackers League Logo - Full Size"
              className="w-full h-auto rounded-xl mt-8"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeader;
